import type { CSSProperties, ReactElement } from 'react';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { cssMap, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const expandSecondCard = keyframes({
	from: {
		transform: `translateY(calc(var(--stack-offset) + ${token('space.100')})) rotate(-2deg) scale(0.97) scaleY(var(--stack-height-ratio))`,
	},
	// Overshoot slightly, then return directly to the resting position.
	'72%': {
		transform: `translateY(${token('space.050')})`,
	},
	to: {
		transform: 'none',
	},
});

const expandThirdCard = keyframes({
	from: {
		transform: `translateY(calc(var(--stack-offset) + ${token('space.200')})) rotate(-4deg) scale(0.94) scaleY(var(--stack-height-ratio))`,
	},
	// Overshoot slightly, then return directly to the resting position.
	'72%': {
		transform: `translateY(${token('space.050')})`,
	},
	to: {
		transform: 'none',
	},
});

const fadeBackingShadow = keyframes({
	from: { boxShadow: token('elevation.shadow.overlay') },
	to: { boxShadow: 'none' },
});

const styles = cssMap({
	stack: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	card: {
		position: 'relative',
		transformOrigin: 'top center',
		borderRadius: token('radius.xlarge'),
	},
	frontCard: {
		zIndex: 3,
		animationName: fadeBackingShadow,
	},
	frontSelectedCard: {
		animationName: 'none',
	},
	secondCard: {
		animationName: `${expandSecondCard}, ${fadeBackingShadow}`,
		zIndex: 2,
	},
	secondSelectedCard: {
		animationName: expandSecondCard,
	},
	thirdCard: {
		animationName: `${expandThirdCard}, ${fadeBackingShadow}`,
		zIndex: 1,
	},
	thirdSelectedCard: {
		animationName: expandThirdCard,
	},
	hiddenCard: {
		animationName: `${expandThirdCard}, ${token('motion.keyframe.fade.in')}`,
		zIndex: 0,
	},
	entering: {
		animationDuration: `var(--stack-duration, ${token('motion.duration.xxlong')})`,
		animationDelay: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationFillMode: 'backwards',
		// Zero duration and delay remove visible motion while still firing animationend
		// to clear the stack styles and allow the selected card to scroll into view.
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: token('motion.duration.instant'),
			animationDelay: token('motion.duration.instant'),
		},
	},
});

type StackPosition = {
	offset: string;
	heightRatio: number;
};

export const SuggestionsStack = ({
	children,
	isAnimating,
	onAnimationComplete,
}: {
	children: ReactElement<{ isSelected?: boolean }>[];
	isAnimating: boolean;
	onAnimationComplete: () => void;
}): React.JSX.Element => {
	const stackRef = useRef<HTMLDivElement>(null);
	const [positions, setPositions] = useState<Map<string, StackPosition>>();

	useLayoutEffect(() => {
		// The parent owns whether this opening should animate. A filter-driven
		// remount after completion must keep the list expanded.
		if (!isAnimating) {
			setPositions(undefined);
			return;
		}
		const cards = stackRef.current?.children;
		if (!cards || cards.length < 2) {
			onAnimationComplete();
			return;
		}

		// Measure once before paint. Filtering, selection and regeneration must not
		// replay the entrance; reopening the panel mounts a fresh stack.
		const firstCard = cards[0];
		if (!(firstCard instanceof HTMLElement)) {
			return;
		}
		const firstCardHeight = firstCard.offsetHeight;
		const initialPositions = new Map<string, StackPosition>();
		Array.from(cards).forEach((card) => {
			if (!(card instanceof HTMLElement) || !card.dataset.stackId) {
				return;
			}
			// Only two backing cards are visible in the opening fan. Match their
			// height to the front card so longer descriptions do not extend too far.
			initialPositions.set(card.dataset.stackId, {
				offset: `${-card.offsetTop}px`,
				heightRatio: card.offsetHeight > 0 ? firstCardHeight / card.offsetHeight : 1,
			});
		});
		setPositions(initialPositions);
	}, [isAnimating, onAnimationComplete]);

	return (
		<div
			ref={stackRef}
			css={styles.stack}
			onAnimationEnd={(event) => {
				// Only card movement completes the stack, not shadows or animations inside a card.
				if (
					event.nativeEvent.pseudoElement ||
					(event.target as HTMLElement).parentElement !== stackRef.current
				) {
					return;
				}

				setPositions(undefined);
				onAnimationComplete();
			}}
		>
			{children.map((child, index) => {
				const isSelected = child.props.isSelected;

				return (
					<div
						key={child.key}
						data-stack-id={String(child.key)}
						css={[
							styles.card,
							positions?.has(String(child.key)) && styles.entering,
							positions?.has(String(child.key)) && index === 0 && styles.frontCard,
							positions?.has(String(child.key)) && index === 1 && styles.secondCard,
							positions?.has(String(child.key)) && index === 2 && styles.thirdCard,
							positions?.has(String(child.key)) && index > 2 && styles.hiddenCard,
							positions?.has(String(child.key)) &&
								isSelected &&
								index === 0 &&
								styles.frontSelectedCard,
							positions?.has(String(child.key)) &&
								isSelected &&
								index === 1 &&
								styles.secondSelectedCard,
							positions?.has(String(child.key)) &&
								isSelected &&
								index === 2 &&
								styles.thirdSelectedCard,
						]}
						style={
							{
								'--stack-duration':
									(positions?.size ?? 0) >= 4 ? '800ms' : token('motion.duration.xxlong'),
								'--stack-offset': positions?.get(String(child.key))?.offset,
								'--stack-height-ratio': positions?.get(String(child.key))?.heightRatio,
							} as CSSProperties
						}
					>
						{child}
					</div>
				);
			})}
		</div>
	);
};
